import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Spin, Statistic, Table, Tag, Typography } from 'antd';
import {
  DollarCircleOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import {
  formatCurrency,
  formatPlanLabel,
  getPlanAmount,
  hasPaidSubscription,
  isAdminCreatedUser,
} from '../utils/subscriptions';

const { Title, Paragraph } = Typography;

const Dashboard = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        setLoading(true);
        const res = await axios.get('https://app-swingervip-production.up.railway.app/api/users');
        setUsuarios(res.data || []);
        setError('');
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, []);

  const dashboardData = useMemo(() => {
    const usuariosAdmin = usuarios.filter(isAdminCreatedUser);
    const usuariosSelf = usuarios.filter((usuario) => !isAdminCreatedUser(usuario));
    const usuariosConPago = usuariosSelf.filter(hasPaidSubscription);

    const montoTotal = usuariosConPago.reduce(
      (total, usuario) => total + getPlanAmount(usuario.plan),
      0
    );

    const planesMap = usuariosConPago.reduce((acc, usuario) => {
      const key = usuario.plan || 'sin-plan';

      if (!acc[key]) {
        acc[key] = {
          key,
          plan: formatPlanLabel(usuario.plan),
          usuarios: 0,
          monto: 0,
        };
      }

      acc[key].usuarios += 1;
      acc[key].monto += getPlanAmount(usuario.plan);

      return acc;
    }, {});

    const planesRows = Object.values(planesMap).sort((a, b) => b.monto - a.monto);

    return {
      usuariosAdmin,
      usuariosSelf,
      usuariosConPago,
      montoTotal,
      planesRows,
    };
  }, [usuarios]);

  const latestUsersColumns = [
    {
      title: 'Nombre',
      key: 'nombre',
      render: (_, record) => `${record.nombre} ${record.apellido}`,
    },
    {
      title: 'Correo',
      dataIndex: 'correo_electronico',
      key: 'correo_electronico',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => formatPlanLabel(plan),
    },
    {
      title: 'Monto',
      dataIndex: 'plan',
      key: 'monto',
      render: (plan) => formatCurrency(getPlanAmount(plan)),
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => (
        <Tag color={record.estado === 'activo' ? 'green' : 'red'}>
          {record.estado?.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const planColumns = [
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
    },
    {
      title: 'Usuarios',
      dataIndex: 'usuarios',
      key: 'usuarios',
    },
    {
      title: 'Monto cobrado',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => formatCurrency(monto),
    },
  ];

  if (loading) {
    return (
      <div className="admin-dashboard-page">
        <div className="admin-dashboard-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-hero">
        <div>
          <Title level={3} style={{ marginBottom: 8 }}>
            Dashboard
          </Title>
          <Paragraph style={{ marginBottom: 0, color: '#6b7280' }}>
            Resumen de usuarios y suscripciones registrado desde el panel.
          </Paragraph>
        </div>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 20 }}
        />
      ) : null}

      <Row gutter={[16, 16]} className="admin-dashboard-stats">
        <Col xs={24} sm={12} xl={6}>
          <Card className="admin-dashboard-stat-card">
            <Statistic
              title="Monto cobrado"
              value={dashboardData.montoTotal}
              precision={2}
              prefix={<DollarCircleOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="admin-dashboard-stat-card">
            <Statistic
              title="Usuarios con pago"
              value={dashboardData.usuariosConPago.length}
              prefix={<WalletOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="admin-dashboard-stat-card">
            <Statistic
              title="Cuenta propia"
              value={dashboardData.usuariosSelf.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="admin-dashboard-stat-card">
            <Statistic
              title="Creados por admin"
              value={dashboardData.usuariosAdmin.length}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Card title="Planes contratados" className="admin-dashboard-table-card">
            <Table
              rowKey="key"
              dataSource={dashboardData.planesRows}
              columns={planColumns}
              pagination={false}
              locale={{ emptyText: 'No hay pagos registrados todavía.' }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card
            title="Usuarios de cuenta propia"
            extra={<span>{dashboardData.usuariosSelf.length} usuarios</span>}
            className="admin-dashboard-table-card"
          >
            <Table
              rowKey="id"
              dataSource={dashboardData.usuariosSelf}
              columns={latestUsersColumns}
              pagination={{ pageSize: 6 }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No hay usuarios autoregistrados.' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
